import { Box, Button, IconButton, Paper, TextField, Typography, CircularProgress, MenuItem, Chip, Stack, Tooltip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import RefreshIcon from '@mui/icons-material/Refresh';
import Loader from '../../../Components/Loader'
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import { Colors } from '../../../Styles/Colors';
import { useHistory, useLocation } from 'react-router-dom';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import ConsentHtmlEditor from './ConsentHtmlEditor';
import { DeletePopup } from '../../../Components/Common Components/CommonComponents';
import { CheckAccessFunc } from '../../../Components/CommonFunctions/CommonFunctions';

class EditorErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null } }
    static getDerivedStateFromError(error) { return { error } }
    componentDidCatch(error, info) { console.error('ConsentHtmlEditor crashed:', error, info) }
    render() {
        if (this.state.error) {
            return (
                <Box sx={{ p: 2, color: 'error.main' }}>
                    <Typography fontWeight="bold" mb={1}>Editor failed to load</Typography>
                    <Typography fontSize="0.85vw" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
                    </Typography>
                    <Button sx={{ mt: 1 }} variant="outlined" size="small"
                        onClick={() => this.setState({ error: null })}>Retry</Button>
                </Box>
            )
        }
        return this.props.children
    }
}

// Patient/admission keys the backend exposes; mirrors SUPPORTED_PLACEHOLDER_KEYS
// in front_office/consent_forms/services.py
const SYSTEM_KEY_OPTIONS = [
    { value: '', label: 'Select (manual entry)' },
    { value: 'patient_name', label: 'Patient Name' },
    { value: 'patient_age', label: 'Patient Age' },
    { value: 'patient_gender', label: 'Patient Gender' },
    { value: 'uhid', label: 'UHID' },
    { value: 'father_or_spouse_name', label: 'Father / Spouse Name' },
    { value: 'relation_of_patient', label: 'Relation of Patient' },
    { value: 'dob', label: 'Date of Birth' },
    { value: 'mobile_number', label: 'Mobile Number' },
    { value: 'address', label: 'Address' },
    { value: 'ip_no', label: 'IP Number' },
    { value: 'bed', label: 'Ward / Bed' },
    { value: 'doctor_name', label: 'Doctor Name' },
    { value: 'hospital_name', label: 'Hospital Name' },
    { value: 'hospital_address', label: 'Hospital Address' },
    { value: 'diagnosis', label: 'Diagnosis / Complaints' },
    { value: 'current_date', label: "Today's Date" },
]

const STATUS_COLOR = {
    uploaded: 'default',
    extracting: 'info',
    draft: 'warning',
    published: 'success',
    failed: 'error',
}

// Only English is offered for now — non-English forms have known alignment
// and table column-width issues in the renderer that need a separate fix.
const LANGUAGE_OPTIONS = [
    { code: 'en', label: 'English' },
]

export default function ConsentFormAdd() {
    const history = useHistory();
    const location = useLocation();
    const templateId = new URLSearchParams(location.search).get('id');

    const [printData, setPrintData] = useState({
        errorMsg: false, errorMsgText: '',
        successMsg: false, successMsgText: '',
        isloader: false,
        template: null,
        extractedHtml: '',           // mirror of template.extracted_html for manual edit
        htmlDirty: false,            // user typed in the HTML editor
        metaDirty: false,            // user changed name/code/language but hasn't saved
        consentName: '',
        consentCode: '',
        language: 'en',
        saving: false,
        publishing: false,
        previewUrl: '',
        previewLoader: false,
        reExtractConfirm: false,
        credits: null,
    })

    const errorMessage = (msg) => setPrintData(prev => ({ ...prev, errorMsg: true, errorMsgText: msg }))
    const successMessage = (msg) => setPrintData(prev => ({ ...prev, successMsg: true, successMsgText: msg }))

    const commonDisable = CheckAccessFunc("front_office", "Settings", "Layout Configuration", "Consent Configuration", "SubTab")?.editAccess

    const loadTemplate = async () => {
        if (!templateId) return;
        try {
            setPrintData(prev => ({ ...prev, isloader: true }))
            const response = await RestAPIService.getAll(`${Serviceurls.CONSENT_TEMPLATE_DETAIL}${templateId}/`)
            const t = response?.data?.data
            setPrintData(prev => ({
                ...prev,
                template: t,
                consentName: t.name || '',
                consentCode: t.code || '',
                // Force English — only English is supported in the dropdown
                // for now (Bug 9). Legacy non-English templates show 'en'
                // here so the restricted dropdown has a valid selection.
                language: 'en',
                extractedHtml: t.extracted_html || '',
                htmlDirty: false,
                metaDirty: false,
                isloader: false,
            }))
            if (t?.extracted_html) loadPreview()
        } catch (e) {
            setPrintData(prev => ({ ...prev, isloader: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const loadCredits = async () => {
        try {
            const response = await RestAPIService.getAll(Serviceurls.CONSENT_FORM_CREDITS)
            setPrintData(prev => ({ ...prev, credits: response?.data?.data || null }))
        } catch (e) {
            // Non-fatal; the BE pre-flight will still 402 if needed.
        }
    }

    const loadPreview = async () => {
        if (!templateId) return;
        try {
            setPrintData(prev => ({ ...prev, previewLoader: true }))
            const response = await RestAPIService.PrintPDF(`${Serviceurls.CONSENT_TEMPLATE_PREVIEW}${templateId}/preview/`)
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = URL.createObjectURL(blob)
            setPrintData(prev => {
                if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl)
                return { ...prev, previewUrl: url, previewLoader: false }
            })
        } catch (e) {
            setPrintData(prev => ({ ...prev, previewLoader: false, previewUrl: '' }))
            // The PrintPDF helper sets responseType: blob, so error response bodies are blobs too.
            // Try to read JSON error message if any, otherwise show a generic toast.
            let msg = e?.message || 'Preview render failed'
            try {
                if (e?.response?.data instanceof Blob) {
                    const text = await e.response.data.text()
                    try {
                        const parsed = JSON.parse(text)
                        if (parsed?.message) msg = parsed.message
                    } catch { msg = text || msg }
                } else if (e?.response?.data?.message) {
                    msg = e.response.data.message
                }
            } catch { /* keep generic msg */ }
            errorMessage('Preview: ' + msg)
        }
    }

    useEffect(() => {
        loadTemplate()
        loadCredits()
        return () => {
            setPrintData(prev => {
                if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl)
                return prev
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId])

    const msgClose = () => setPrintData(prev => ({ ...prev, errorMsg: false, errorMsgText: '', successMsg: false, successMsgText: '' }))

    // Auto-derive the field metadata from placeholder occurrences in the HTML, so
    // the user never has to maintain it separately. Any {{key}} found in the saved
    // HTML becomes a row in extracted_fields_json — auto_populate=true if it's
    // one of the standard system keys, false otherwise (manual entry at issue time).
    const deriveFields = (html) => {
        if (!html) return [];
        const known = new Set(SYSTEM_KEY_OPTIONS.filter(o => o.value).map(o => o.value));
        const labelByKey = Object.fromEntries(SYSTEM_KEY_OPTIONS.map(o => [o.value, o.label]));
        const seen = new Set();
        const out = [];
        const re = /\{\{\s*([a-zA-Z_][\w]*)\s*\}\}/g;
        let m;
        while ((m = re.exec(html)) !== null) {
            const key = m[1];
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({
                label: labelByKey[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                suggested_key: key,
                system_key: known.has(key) ? key : '',
                auto_populate: known.has(key),
            });
        }
        return out;
    }

    const handleHtmlChange = (newHtml) => {
        setPrintData(prev => prev.extractedHtml === newHtml
            ? prev
            : ({ ...prev, extractedHtml: newHtml, htmlDirty: true }))
    }

    const handleSave = async () => {
        if (!printData.template) return;
        if (!printData.consentName.trim()) { errorMessage('Form name is required'); return; }
        try {
            setPrintData(prev => ({ ...prev, saving: true }))
            const payload = {
                name: printData.consentName.trim(),
                code: printData.consentCode.trim(),
                language: printData.language,
                extracted_html: printData.extractedHtml,
                extracted_fields_json: deriveFields(printData.extractedHtml),
                _nonce: Date.now(),
            }
            await RestAPIService.patch(payload, `${Serviceurls.CONSENT_TEMPLATE_DETAIL}${templateId}/`)
            successMessage('Saved')
            setPrintData(prev => ({ ...prev, saving: false, htmlDirty: false, metaDirty: false }))
            loadTemplate()
        } catch (e) {
            setPrintData(prev => ({ ...prev, saving: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const handlePublish = async () => {
        try {
            setPrintData(prev => ({ ...prev, publishing: true }))
            await RestAPIService.create({ _nonce: Date.now() }, `${Serviceurls.CONSENT_TEMPLATE_PUBLISH}${templateId}/publish/`)
            successMessage('Template published')
            setPrintData(prev => ({ ...prev, publishing: false }))
            loadTemplate()
        } catch (e) {
            setPrintData(prev => ({ ...prev, publishing: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const openReExtractConfirm = () => setPrintData(prev => ({ ...prev, reExtractConfirm: true }))
    const closeReExtractConfirm = () => setPrintData(prev => ({ ...prev, reExtractConfirm: false }))

    const handleReExtract = async () => {
        closeReExtractConfirm()
        try {
            await RestAPIService.create({ _nonce: Date.now() }, `${Serviceurls.CONSENT_TEMPLATE_REEXTRACT}${templateId}/re-extract/`)
            successMessage('Re-extraction complete')
            loadTemplate()
            loadCredits()
        } catch (e) {
            errorMessage(e?.response?.data?.message || e.message)
            // 402 — refresh to update the chip in case a sibling clinic drained the pool.
            if (e?.response?.status === 402) loadCredits()
        }
    }

    const reExtractWarning = () => {
        if (printData.template?.status === 'published') {
            return {
                title: 'Re-extract this published form?',
                content: 'Re-extracting will move this form back to Draft and rebuild it from the original upload. Any changes you have made to the layout will be lost, and you will need to publish it again.',
                alert: 'The form will be moved back to Draft.',
            }
        }
        if (hasUnsavedChanges) {
            return {
                title: 'Re-extract this form?',
                content: 'You have unsaved changes. Re-extraction will rebuild the form from the original upload and your unsaved changes will be lost.',
                alert: 'Unsaved changes will be lost.',
            }
        }
        return {
            title: 'Re-extract this form?',
            content: 'Re-running AI extraction will rebuild the form from the original upload. Any earlier changes to the layout will be lost.',
        }
    }

    const t = printData.template;
    const isExtracting = t && (t.status === 'uploaded' || t.status === 'extracting');
    const canEdit = t && (t.status === 'draft' || t.status === 'failed');
    const hasUnsavedChanges = printData.htmlDirty || printData.metaDirty;
    const canPublish = t && t.status === 'draft' && !hasUnsavedChanges;
    const credits = printData.credits;
    const hasCredits = !!credits && credits.enabled && credits.is_active && credits.remaining_credits > 0;
    const reExtractBlockedReason = !credits || !credits.enabled
        ? 'AI Consent Form is not enabled for this branch — contact support to purchase credits.'
        : !credits.is_active
            ? 'AI Consent Form has been suspended for this branch.'
            : credits.remaining_credits <= 0
                ? 'AI Consent Form credits exhausted — please top up to continue.'
                : '';

    const renderHtmlTab = () => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <EditorErrorBoundary>
                    <ConsentHtmlEditor
                        value={printData.extractedHtml}
                        onChange={handleHtmlChange}
                        disabled={!canEdit}
                        placeholderOptions={SYSTEM_KEY_OPTIONS}
                    />
                </EditorErrorBoundary>
            </Box>
        </Box>
    )

    return (
        <Box sx={{ height: '85vh', width: '94vw', backgroundColor: Colors.Background, padding: '.5vw',
                   display: 'flex', flexDirection: 'column', gap: '0.6vw' }}>
            {/* Top header card: back button + title + status chips on the right */}
            <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', padding: '0.4vw 0.6vw', backgroundColor: 'white' }}>
                <IconButton size="small" onClick={() => history.push('/Settings/LayoutConfig/ConsentFormConfiguration')}>
                    <ChevronLeftIcon color='primary' />
                </IconButton>
                <Typography color={Colors.SecondaryText} fontWeight={600} fontSize={'0.95vw'} sx={{ flex: 1 }}>
                    Consent Form Configuration
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    {t && <Chip size="small" label={t.status?.toUpperCase()} color={STATUS_COLOR[t.status] || 'default'} />}
                    {t?.version && <Chip size="small" variant="outlined" label={`v${t.version}`} />}
                    {t?.published_at && (
                        <Chip size="small" color="success" variant="outlined"
                              label={`Published ${new Date(t.published_at).toLocaleDateString()}`} />
                    )}
                </Stack>
            </Paper>

            {/* Form details card: name + code + language */}
            <Paper elevation={1} sx={{ padding: '0.6vw 0.8vw', backgroundColor: 'white',
                                       display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.8vw' }}>
                <TextField size="small" sx={{ flex: '0 0 22vw' }} label="Form Name *"
                    value={printData.consentName} disabled={!canEdit}
                    onChange={(e) => setPrintData(prev => ({ ...prev, consentName: e.target.value, metaDirty: true }))} />
                <TextField size="small" sx={{ flex: '0 0 9vw' }} label="Code"
                    value={printData.consentCode} disabled={!canEdit}
                    onChange={(e) => setPrintData(prev => ({ ...prev, consentCode: e.target.value, metaDirty: true }))} />
                <TextField select size="small" sx={{ flex: '0 0 11vw' }} label="Language"
                    value={printData.language} disabled={!canEdit}
                    onChange={(e) => setPrintData(prev => ({ ...prev, language: e.target.value, metaDirty: true }))}>
                    {LANGUAGE_OPTIONS.map(opt => (
                        <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                    ))}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={reExtractBlockedReason || (t?.status === 'published'
                    ? 'Re-run AI extraction (will revert this template to Draft)'
                    : 'Re-run AI extraction (overwrites current edits)')}>
                    <span>
                        <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
                                onClick={openReExtractConfirm} disabled={!t?.source_s3_key || isExtracting || !commonDisable || !hasCredits}
                                sx={{ textTransform: 'none' }}>
                            Re-extract
                        </Button>
                    </span>
                </Tooltip>
            </Paper>

            <Box sx={{ flex: 1, display: 'flex', gap: '0.6vw', minHeight: 0 }}>
                <Paper elevation={2} sx={{
                    flex: '0 0 32%', maxWidth: '32%',
                    display: 'flex', flexDirection: 'column',
                }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: '0.5vw 0.8vw', borderBottom: '1px solid lightGray' }}>
                        <Typography fontSize="0.9vw" fontWeight={600} color={Colors.SecondaryText}>Digital Preview</Typography>
                        <Tooltip title="Refresh preview">
                            <IconButton size="small" onClick={loadPreview} disabled={!t?.extracted_html}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
                        {printData.previewLoader
                            ? <CircularProgress color="primary" size={'2vw'} />
                            : printData.previewUrl
                                ? <iframe style={{ width: '100%', height: '100%', border: 'none' }} src={`${printData.previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title="Digital preview" />
                                : <Typography fontSize={'1vw'}>{isExtracting ? 'Extracting…' : 'Preview will appear after extraction'}</Typography>}
                    </Box>
                </Paper>
                <Paper elevation={2} sx={{
                    flex: '0 0 68%', maxWidth: '68%',
                    display: 'flex', flexDirection: 'column',
                }}>
                    <Box sx={{ padding: '0.5vw 0.8vw', borderBottom: '1px solid lightGray',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography fontSize="0.9vw" fontWeight={600} color={Colors.SecondaryText}>
                            Edit Layout{hasUnsavedChanges ? ' *' : ''}
                        </Typography>
                        {isExtracting && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={14} />
                                <Typography fontSize="0.75vw" color="text.secondary">AI is extracting…</Typography>
                            </Stack>
                        )}
                        {t?.status === 'failed' && (
                            <Typography fontSize="0.75vw" color="error.main">
                                Extraction failed. {t.extraction_error || ''}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {renderHtmlTab()}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '0.5vw 0.6vw', gap: '0.5vw',
                                borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                        <Typography fontSize="0.75vw" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {hasUnsavedChanges
                                ? 'You have unsaved changes. Click Save before publishing.'
                                : (canPublish ? 'Review and click Publish when ready.' : '')}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button variant='text' size='small' sx={{ textTransform: 'none' }}
                                onClick={() => loadTemplate()}>Refresh</Button>
                            <Button variant='outlined' size='small' sx={{ textTransform: 'none', minWidth: '7vw' }}
                                onClick={handleSave} disabled={!canEdit || printData.saving || !commonDisable}>
                                {printData.saving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant='contained' size='small' sx={{ textTransform: 'none', minWidth: '7vw' }}
                                onClick={handlePublish} disabled={!canPublish || printData.publishing || !commonDisable}>
                                {printData.publishing ? 'Publishing…' : 'Publish'}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Box>

            {printData.errorMsg && <ToastMsg severity={'error'} msg={printData.errorMsgText} msgPop={msgClose} />}
            {printData.successMsg && <ToastMsg severity={'success'} msg={printData.successMsgText} msgPop={msgClose} />}
            {printData.reExtractConfirm && (() => {
                const w = reExtractWarning();
                return (
                    <DeletePopup
                        DeletTitle={w.title}
                        DeletContent={w.content}
                        AlertNote={w.alert}
                        ButtonText={'Re-extract'}
                        deleteAlertPopupClose={closeReExtractConfirm}
                        removeData={handleReExtract}
                    />
                )
            })()}
            <Loader loaderOpen={printData.isloader} />
        </Box>
    )
}
